/**
 * DateTimePickerModal Component
 * 
 * A cross-platform date and time picker modal that works consistently on iOS and Android.
 * Originally created for Events, now reusable across Events and Bulletins.
 * 
 * Features:
 * - iOS: Custom modal with spinner-style picker
 * - Android: Native date/time picker dialogs
 * - Support for multiple picker types: 'start', 'end', 'publish'
 * - Configurable titles and confirmation callbacks
 * - Consistent styling with admin theme
 * - Proper state management for temporary date selection
 * 
 * Platform Differences:
 * - iOS: Shows as bottom modal with Cancel/Done buttons
 * - Android: Shows native picker dialogs with automatic mode switching
 * 
 * Usage:
 * ```tsx
 * <DateTimePickerModal
 *   DateTimePicker={DateTimePicker}
 *   datePickerState={datePickerState}
 *   onDateChange={handleDateChange}
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 *   pickerType="publish"
 *   title="Select Publish Date"
 *   onModeChange={handleModeChange}
 * />
 * ```
 */
import React, { memo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { COLORS } from '@/constants/residents';

interface DatePickerState {
  tempDate: Date;
  showStartDatePicker: boolean;
  showEndDatePicker: boolean;
  showPublishDatePicker: boolean;
  datePickerMode: 'date' | 'time';
}

interface DateTimePickerModalProps {
  DateTimePicker: any;
  datePickerState: DatePickerState;
  onDateChange: (event: any, selectedDate?: Date) => void;
  onConfirm: (pickerType: 'start' | 'end' | 'publish') => void;
  onCancel: () => void;
  pickerType: 'start' | 'end' | 'publish';
  title: string;
  onModeChange?: (mode: 'date' | 'time') => void;
  // Web-only: anchor for dropdown
  webAnchor?: AnchorRect | null;
  // When true, restricts picking to date only (no time)
  dateOnly?: boolean;
}

export const DateTimePickerModal = memo<DateTimePickerModalProps>(({
  DateTimePicker,
  datePickerState,
  onDateChange,
  onConfirm,
  onCancel,
  pickerType,
  title,
  onModeChange,
  webAnchor,
  dateOnly = false,
}) => {
  if (!DateTimePicker && Platform.OS !== 'web') {
    return null;
  }

  const isVisible = 
    (pickerType === 'start' && datePickerState.showStartDatePicker) ||
    (pickerType === 'end' && datePickerState.showEndDatePicker) ||
    (pickerType === 'publish' && datePickerState.showPublishDatePicker);

  if (!isVisible) {
    return null;
  }

  // Web: render a custom overlay with a native datetime-local input
  if (Platform.OS === 'web') {
    const toInputValue = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      if (dateOnly) {
        return `${y}-${m}-${d}`;
      }
      const hh = String(date.getHours()).padStart(2, '0');
      const mm = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${d}T${hh}:${mm}`;
    };

    const [value, setValue] = useState<string>(toInputValue(datePickerState.tempDate));

    const handleChange = (e: any) => {
      const raw = e.target.value;
      const str = dateOnly ? `${raw}T00:00` : raw;
      const next = new Date(str);
      if (!isNaN(next.getTime())) {
        setValue(raw);
        // Call onDateChange so upstream tempDate is kept in sync
        onDateChange({ type: 'set', nativeEvent: {} } as any, next);
      }
    };

    return (
      <AnchoredDropdown isOpen={true} anchor={webAnchor ?? null} width={320} maxHeight={380}>
        <View style={{ padding: 12 }}>
          <Text style={styles.datePickerTitle}>{title}</Text>
          {/* eslint-disable-next-line react/no-unknown-property */}
          {/* @ts-ignore - using raw input for web */}
          <input
            type={dateOnly ? "date" : "datetime-local"}
            value={value}
            onChange={handleChange}
            style={{
              width: '100%',
              height: 40,
              padding: 8,
              color: COLORS.TEXT_WHITE,
              background: 'transparent',
              border: `1px solid ${ADMIN_COLORS.BORDER}`,
              borderRadius: 8,
              marginTop: 8,
              marginBottom: 12,
            }}
          />
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8 }}>
            <TouchableOpacity onPress={onCancel}>
              <Text style={styles.datePickerButton}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => onConfirm(pickerType)}>
              <Text style={styles.datePickerButton}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </AnchoredDropdown>
    );
  }

  if (Platform.OS === 'ios') {
    return (
      <View style={styles.datePickerOverlay}>
        <TouchableOpacity 
          style={[StyleSheet.absoluteFillObject, { zIndex: 1 }]}
          onPress={onCancel}
        />
        <View style={[styles.datePickerModal, { zIndex: 2, position: 'absolute', left: 0, right: 0, bottom: 0 }]}>
          <View style={styles.datePickerHeader}>
            <TouchableOpacity onPress={onCancel}>
              <Text style={styles.datePickerButton}>Cancel</Text>
            </TouchableOpacity>
            <Text style={styles.datePickerTitle}>{title}</Text>
            <TouchableOpacity onPress={() => onConfirm(pickerType)}>
              <Text style={styles.datePickerButton}>Done</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.datePickerContent}>
            <DateTimePicker
              value={datePickerState.tempDate}
              mode={dateOnly ? 'date' : 'datetime'}
              display="spinner"
              onChange={onDateChange}
              style={styles.dateTimePicker}
              textColor={ADMIN_COLORS.TEXT_PRIMARY}
            />
          </View>
        </View>
      </View>
    );
  }

  // Android DateTimePicker
  return (
    <DateTimePicker
      value={datePickerState.tempDate}
      mode={dateOnly ? 'date' : datePickerState.datePickerMode}
      display="default"
      textColor={ADMIN_COLORS.TEXT_PRIMARY}
      onChange={(event: any, date?: Date) => {
        if (!date) {
          onCancel();
          onModeChange?.('date');
          return;
        }
        // Always apply the selected date
        onDateChange(event, date);
        if (dateOnly) {
          // Immediately confirm for date-only
          onConfirm(pickerType);
          onModeChange?.('date');
        } else if (onModeChange) {
          // Two-step selection (date -> time)
          if (datePickerState.datePickerMode === 'date') {
            onModeChange('time');
          } else {
            onConfirm(pickerType);
            onModeChange('date');
          }
        }
      }}
    />
  );
});

DateTimePickerModal.displayName = 'DateTimePickerModal';

const styles = StyleSheet.create({
  datePickerModal: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 34, // Safe area padding
    minHeight: 300,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  datePickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  datePickerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  datePickerButton: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.PRIMARY,
  },
  datePickerContent: {
    padding: 16,
  },
  dateTimePicker: {
    height: 200,
  },
  datePickerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
    zIndex: 9999,
  },
});
